import type { ReactNode } from "react";
import runner400 from "@/assets/home/run-3-mile-400.webp";
import runner800 from "@/assets/home/run-3-mile-800.webp";
import runnerFull from "@/assets/home/run-3-mile-full.webp";
import { PreviewsFirstAnimation } from "@/components/webUploader/PreviewsFirstAnimation";
import { getWebUploaderCopy } from "@/content/webUploader";
import { InvitedUploadAnimation, SaleSplitAnimation } from "./GuideAnimations";

// The guides' diagrams (":::diagram <id>" in a guide). Each is HTML + small inline SVG, so it reflows on a phone
// (steps stack) instead of shrinking a wide picture until its text can't be read, and it has a text equivalent
// for screen readers. They show how things work, never results we haven't measured.

const Svg = ({ children, label }: { children: ReactNode; label?: string }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden={label ? undefined : true} role={label ? "img" : undefined} aria-label={label}>
    {children}
  </svg>
);

const ICONS = {
  upload: (
    <Svg>
      <path d="M12 16V4M7 9l5-5 5 5M4 16v3a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-3" />
    </Svg>
  ),
  shoot: (
    <Svg>
      <rect x="3" y="7" width="18" height="13" rx="2" />
      <circle cx="12" cy="13.5" r="3.5" />
      <path d="M8 7l1.5-3h5L16 7" />
    </Svg>
  ),
  live: (
    <Svg>
      <circle cx="12" cy="12" r="3" />
      <path d="M5.6 5.6a9 9 0 0 0 0 12.8M18.4 5.6a9 9 0 0 1 0 12.8M8.5 8.5a5 5 0 0 0 0 7M15.5 8.5a5 5 0 0 1 0 7" />
    </Svg>
  ),
  cart: (
    <Svg>
      <circle cx="9" cy="20" r="1.4" />
      <circle cx="18" cy="20" r="1.4" />
      <path d="M2 3h3l2.6 12.2a1 1 0 0 0 1 .8h9.7a1 1 0 0 0 1-.8L21 7H6" />
    </Svg>
  ),
  download: (
    <Svg>
      <path d="M12 4v12M7 11l5 5 5-5M4 20h16" />
    </Svg>
  ),
  face: (
    <Svg>
      <path d="M4 8V5a1 1 0 0 1 1-1h3M16 4h3a1 1 0 0 1 1 1v3M20 16v3a1 1 0 0 1-1 1h-3M8 20H5a1 1 0 0 1-1-1v-3" />
      <circle cx="9.5" cy="10" r=".6" fill="currentColor" />
      <circle cx="14.5" cy="10" r=".6" fill="currentColor" />
      <path d="M9 15c1.6 1.3 4.4 1.3 6 0" />
    </Svg>
  ),
  numbers: (
    <Svg>
      <path d="M4 9h16M4 15h16M10 3 8 21M16 3l-2 18" />
    </Svg>
  ),
  compare: (
    <Svg>
      <path d="M7 4v16M17 4v16M3 8h8M13 16h8" />
    </Svg>
  ),
  bin: (
    <Svg>
      <path d="M4 7h16M10 11v6M14 11v6M6 7l1 13h10l1-13M9 7V4h6v3" />
    </Svg>
  ),
  photos: (
    <Svg>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <circle cx="9" cy="10" r="1.6" />
      <path d="m21 16-5-5-8 8" />
    </Svg>
  ),
  wallet: (
    <Svg>
      <path d="M4 7h14a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a1 1 0 0 1-1-1V7Zm0 0 11-3v3M16 13.5h2" />
    </Svg>
  ),
  clock: (
    <Svg>
      <circle cx="12" cy="12" r="8" />
      <path d="M12 8v4l3 2" />
    </Svg>
  ),
  lock: (
    <Svg>
      <rect x="5" y="11" width="14" height="9" rx="2" />
      <path d="M8 11V8a4 4 0 0 1 8 0v3" />
    </Svg>
  ),
};

type IconId = keyof typeof ICONS;

interface Step {
  icon: IconId;
  title: string;
  body: string;
  /** A plan or condition label shown on the step. */
  tag?: string;
}

/** Numbered steps in a row on wide screens, a column on a phone. */
const Flow = ({ label, steps }: { label: string; steps: Step[] }) => (
  <ol className="guide-flow" aria-label={label}>
    {steps.map((s, i) => (
      <li key={s.title} className="guide-flow__step">
        <span className="guide-flow__icon">{ICONS[s.icon]}</span>
        <span className="guide-flow__n" aria-hidden="true">
          {i + 1}
        </span>
        <span className="guide-flow__title">{s.title}</span>
        <span className="guide-flow__body">{s.body}</span>
        {s.tag && <span className="guide-flow__tag">{s.tag}</span>}
      </li>
    ))}
  </ol>
);

/** Two lanes side by side (stacked on a phone), e.g. what happens at upload vs at search. */
const Lanes = ({ label, lanes }: { label: string; lanes: { title: string; steps: Step[] }[] }) => (
  <div className="guide-lanes" role="group" aria-label={label}>
    {lanes.map((lane) => (
      <div key={lane.title} className="guide-lanes__lane">
        <p className="guide-lanes__title">{lane.title}</p>
        <ol>
          {lane.steps.map((s) => (
            <li key={s.title}>
              <span className="guide-flow__icon">{ICONS[s.icon]}</span>
              <span>
                <span className="guide-flow__title">{s.title}</span>
                <span className="guide-flow__body">{s.body}</span>
              </span>
            </li>
          ))}
        </ol>
      </div>
    ))}
  </div>
);

// ---------- Bib reading on a real race photo (CC0, credited in the guide) ----------
// Boxes are in the photo's own pixels (1023 x 684). An illustration of what a bib reader has to work with, not the
// output of Photohawk's detector.
const BIB_MARKS: { n: number; x: number; y: number; w: number; h: number; ok: boolean; text: string }[] = [
  { n: 1, x: 450, y: 343, w: 65, h: 50, ok: true, text: "969: flat, facing the lens, all digits visible. Easy to read." },
  { n: 2, x: 645, y: 343, w: 60, h: 42, ok: true, text: "1348: clear, even though the runner is turning slightly." },
  { n: 3, x: 805, y: 272, w: 45, h: 26, ok: true, text: "697 and 789: small and further back, but still readable in a sharp photo." },
  { n: 4, x: 255, y: 335, w: 90, h: 60, ok: false, text: "A hand across the bib: only part of the number shows, so this runner is a job for selfie search." },
  { n: 5, x: 545, y: 300, w: 30, h: 32, ok: false, text: "Half hidden behind another runner's arm: the first digits are missing." },
];

const BibReading = () => (
  <div>
    <div className="relative overflow-hidden rounded-xl border border-border">
      <img
        src={runner800}
        srcSet={`${runner400} 400w, ${runner800} 800w, ${runnerFull} 1023w`}
        sizes="(min-width: 768px) 680px, 100vw"
        width={1023}
        height={684}
        alt="Runners on a wet road at the 3-mile mark of a half marathon, wearing numbered bibs."
        loading="lazy"
        decoding="async"
        className="block h-auto w-full"
      />
      <svg viewBox="0 0 1023 684" className="absolute inset-0 h-full w-full" aria-hidden="true">
        {BIB_MARKS.map((m) => (
          <g key={m.n}>
            <rect x={m.x} y={m.y} width={m.w} height={m.h} rx="4" fill="none" stroke={m.ok ? "#22C55E" : "#FF6B2C"} strokeWidth="4" strokeDasharray={m.ok ? undefined : "10 6"} />
            <circle cx={m.x + m.w} cy={m.y} r="17" fill={m.ok ? "#15803D" : "#C2410C"} stroke="#fff" strokeWidth="3" />
            <text x={m.x + m.w} y={m.y + 7} textAnchor="middle" fontSize="20" fontWeight="700" fill="#fff" fontFamily="Inter, sans-serif">
              {m.n}
            </text>
          </g>
        ))}
      </svg>
    </div>
    <ol className="guide-legend">
      {BIB_MARKS.map((m) => (
        <li key={m.n}>
          <span className={`guide-legend__n ${m.ok ? "guide-legend__n--ok" : "guide-legend__n--miss"}`} aria-hidden="true">
            {m.n}
          </span>
          <span>
            <span className="sr-only">{m.ok ? "Readable: " : "Hard to read: "}</span>
            {m.text}
          </span>
        </li>
      ))}
    </ol>
  </div>
);

// ---------- How a selfie search works ----------
const SelfieSearch = () => (
  <Lanes
    label="How a selfie search works"
    lanes={[
      {
        title: "When the photos are uploaded",
        steps: [
          { icon: "photos", title: "Faces are found in each photo", body: "Every face in the gallery's photos is located." },
          { icon: "face", title: "Each face becomes a face template", body: "A set of numbers describing the face, not a picture of it." },
          { icon: "lock", title: "Templates go into that gallery's index", body: "One index per gallery, used only for searching that gallery." },
        ],
      },
      {
        title: "When you search with a selfie",
        steps: [
          { icon: "face", title: "Your selfie is turned into a template", body: "The same kind of numbers, made from your face." },
          { icon: "compare", title: "It's compared with the gallery's templates", body: "Faces that are close enough count as possible matches." },
          { icon: "bin", title: "The selfie is discarded", body: "It isn't stored or added to any index. You see the matching photos." },
        ],
      },
    ]}
  />
);

// ---------- From shoot to delivered originals ----------
const SellWorkflow = () => (
  <Flow
    label="From the shoot to delivered originals"
    steps={[
      { icon: "shoot", title: "Shoot", body: "Cover the moments people buy: start, course, finish." },
      { icon: "upload", title: "Upload", body: "Add the folder to the web uploader, on any plan." },
      { icon: "live", title: "Previews live", body: "Light previews go up first, so the gallery goes live while the originals follow.", tag: "Every plan" },
      { icon: "cart", title: "Orders", body: "People find themselves by selfie or bib, then buy or download." },
      { icon: "download", title: "Originals", body: "Buyers get the originals. A bought photo's original jumps the queue.", tag: "Queue jump: every plan" },
    ]}
  />
);

// ---------- When face data is deleted (from the biometric data retention policy) ----------
const RetentionTimeline = () => (
  <div className="guide-retention" role="group" aria-label="When Photohawk deletes face data: at the earliest of these">
    <p className="guide-lanes__title">Face data is deleted at the earliest of:</p>
    <ul>
      <li>
        <strong>12 months</strong>
        <span>after each photo was uploaded, photo by photo, even if the gallery stays online</span>
      </li>
      <li>
        <strong>30 days</strong>
        <span>after the gallery is closed or deleted, or face search is switched off for it</span>
      </li>
      <li>
        <strong>30 days</strong>
        <span>after a person asks for their face data to be deleted, or withdraws consent</span>
      </li>
      <li>
        <strong>45 days</strong>
        <span>after the yearly review finds it's no longer necessary</span>
      </li>
    </ul>
    <p className="guide-retention__note">Selfies used to search are never stored. Photos, orders and bib search aren't affected.</p>
  </div>
);

// ---------- Which search to use ----------
const SearchChooser = () => (
  <Lanes
    label="Bib search or selfie search"
    lanes={[
      {
        title: "Try your bib number first when…",
        steps: [
          { icon: "numbers", title: "Your bib was on the front and flat", body: "Pinned to your chest or a race belt, not folded or under a jacket." },
          { icon: "photos", title: "The event is big and busy", body: "A number is unique, so it cuts thousands of photos down fast." },
          { icon: "face", title: "You wore sunglasses, a cap or a buff", body: "Your number doesn't care what's on your face." },
        ],
      },
      {
        title: "Use a selfie when…",
        steps: [
          { icon: "face", title: "Your bib was covered or out of sight", body: "Arms, jackets, hydration vests and other runners all hide numbers." },
          { icon: "photos", title: "There were no bibs at all", body: "Weddings, parties, conferences, school and club days." },
          { icon: "compare", title: "You want the photos bib search missed", body: "Run both. Each finds photos the other can't." },
        ],
      },
    ]}
  />
);

// ---------- When a revenue share is paid ----------
const PayoutTimeline = () => (
  <Flow
    label="When a share of a sale is paid"
    steps={[
      { icon: "cart", title: "Sale", body: "Each person's share is worked out and fixed when the order is placed." },
      { icon: "clock", title: "Settling", body: "The sale waits out the settlement window, in case it's refunded.", tag: "3 days" },
      { icon: "wallet", title: "Ready or held", body: "Ready if their payout account is set up; held until it is if not." },
      { icon: "compare", title: "Daily payout run", body: "One payment per person per currency, across every workspace.", tag: "US$25 minimum" },
      { icon: "download", title: "Paid", body: "Sent through Trolley to the account they chose. Smaller amounts roll over." },
    ]}
  />
);

const PreviewsFirst = () => {
  const c = getWebUploaderCopy("en");
  return <PreviewsFirstAnimation copy={c.previews} pause={c.pause} play={c.play} />;
};

const DIAGRAMS: Record<string, () => JSX.Element> = {
  "sale-split": SaleSplitAnimation,
  "payout-timeline": PayoutTimeline,
  "invited-upload": InvitedUploadAnimation,
  "previews-first": PreviewsFirst,
  "bib-reading": BibReading,
  "selfie-search": SelfieSearch,
  "sell-workflow": SellWorkflow,
  "retention-timeline": RetentionTimeline,
  "search-chooser": SearchChooser,
};

export const DIAGRAM_IDS = Object.keys(DIAGRAMS);

export const GuideDiagram = ({ id }: { id: string }) => {
  const D = DIAGRAMS[id];
  return D ? <D /> : null;
};

